import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Message, Button, FormToggle } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Eye, EyeOff } from 'lucide-react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import SideModal from 'components/atoms/sideModal';
import { trackEvent } from '../../../helpers/analytics';
import { activeNetworkSelector, userSelector } from '../../../redux/selectors';
import SharedListLink from './sharedListLink';
import IntroRequest from './introRequest';
import LayoutSelector from './layoutSelector';
import { useFocusTrapState } from '../../../hooks/useFocusTrapState';
import ContactsSharedListSchema from '../../../schemas/contactsSharedList';
import { EnableSharing } from './enableSharing';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { getContactsViewKey } from '../../../helpers/storeKeys';

export const ShareSharedListModal = ({ list, isLoading, onEditSharedList, onClose }) => {
  const { pauseFocusTrap, onDropDownStateChange } = useFocusTrapState();
  const activeNetwork = useSelector(activeNetworkSelector);
  const [pwdVisible, setPwdVisible] = useState(false);
  const [pwdValue, setPwdValue] = useState('');
  const user = useSelector(userSelector);
  const listUrl = `${window.location.origin}/app/shared-lists/${list?.slug}`;
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [showPassword, setShowPassword] = useState(list.passwordProtected);
  const viewKey = getContactsViewKey(activeNetwork, list?.id, false);
  const [currentView] = useLocalStorage(viewKey, 'table');

  const onPasswordEnabled = async () => {
    const done = await onEditSharedList(
      {
        shareable: list.shareable,
        passwordProtected: true,
        password: pwdValue,
        introRequestsEnabled: list.introRequestsEnabled,
        introRequestManagerId: list.introRequestManager?.id,
      },
      {
        closeModal: false,
        updateMessage: 'Shared view settings updated',
      },
    );

    if (done) {
      setPasswordChanged(true);
      trackEvent('contacts:list:enable_password', {
        collection_id: activeNetwork.id,
        list_id: list.id,
      });
    }
  };

  const onPasswordChange = (e) => {
    const { checked } = e.target;

    if (!checked && list.passwordProtected) {
      onEditSharedList(
        {
          shareable: list.shareable,
          passwordProtected: false,
          password: '',
          introRequestsEnabled: list.introRequestsEnabled,
          introRequestManagerId: list.introRequestManager?.id,
        },
        {
          closeModal: false,
        },
      );
      setShowPassword(false);
      setPwdValue('');
      return;
    }

    setShowPassword(!showPassword);
  };

  const onChangeShareable = ({ target }) => {
    onEditSharedList(
      {
        shareable: target.checked,
        passwordProtected: false,
        password: '',
        introRequestsEnabled: list.introRequestsEnabled,
        introRequestManagerId: list.introRequestManager?.id,
      },
      {
        closeModal: false,
      },
    );
    setPwdValue('');
    setShowPassword(false);

    trackEvent('lists:toggle_shareable_url', {
      collectionId: activeNetwork.id,
      listSlug: list.slug,
      listId: list.id,
      objectType: 'contacts',
      action: target.checked ? 'enable' : 'disable',
    });
  };

  const onIntroRequestManagerChange = ({ value }) => {
    onEditSharedList(
      {
        introRequestManagerId: value,
      },
      {
        closeModal: false,
        updateMessage: 'Shared view settings updated',
      },
    );
  };

  const onShowIntroRequestChange = (changedValue) => {
    onEditSharedList(
      {
        introRequestsEnabled: changedValue,
        introRequestManagerId: list.introRequestManager?.id ?? user.id,
      },
      {
        closeModal: false,
        updateMessage: 'Shared view settings updated',
      },
    );

    trackEvent('intro_requests_enabled', {
      collection_id: activeNetwork.id,
      list_id: list.id,
      action: changedValue ? 'enable' : 'disable',
    });
  };

  const onLayoutChange = ({ value }) => {
    onEditSharedList(
      {
        shareableLayout: value,
      },
      {
        closeModal: false,
        updateMessage: 'Shared view settings updated',
      },
    );

    trackEvent('contacts:list:change_shared_layout', {
      collection_id: activeNetwork.id,
      list_id: list.id,
      layout: value,
    });
  };

  const onCloseModal = () => {
    setPwdValue('');
    onClose();
  };

  const enableSharing = () =>
    onEditSharedList(
      {
        shareable: true,
        passwordProtected: false,
        password: '',
        introRequestManagerId: list.introRequestManager?.id,
        introRequestsEnabled: list.introRequestsEnabled,
        shareableLayout: currentView === 'card' ? 'cards_view' : 'table_view',
      },
      {
        closeModal: false,
      },
    ).then(() => {
      trackEvent('contacts:list:save_share_settings', {
        collection_id: activeNetwork.id,
        list_id: list.id,
      });
    });

  return (
    <SideModal width="680px" onClose={onCloseModal} isOpen title="Share list" pauseFocusTrap={pauseFocusTrap}>
      {!list.shareable && (
        <EnableSharing
          listName={list.name}
          onEnableSharing={() => {
            enableSharing().catch(() => {
              toast('Failed to enable sharing', {
                icon: 'error',
                id: 'enable-sharing-error',
              });
            });
          }}
          isLoading={isLoading}
        />
      )}
      {list.shareable && (
        <Box sx={{ borderRadius: '4px', mt: '32px' }}>
          <Flex sx={{ gap: '32px', flexDirection: 'column' }}>
            <Flex sx={{ gap: '8px', flexDirection: 'column' }}>
              <Text
                color="text.subtle"
                fontSize="12px"
                fontWeight="400"
                lineHeight="16px"
                letterSpacing="1px"
                sx={{ textTransform: 'uppercase' }}
              >
                Shared view URL
              </Text>
              <SharedListLink listUrl={listUrl} />
              <Flex sx={{ mt: '8px', gap: '16px', flexDirection: 'column' }}>
                <FormToggle
                  label="Password protection"
                  onChange={onChangeShareable}
                  helpText="Require a password for visitors to view the list."
                  field={{
                    id: 'pwd-protection',
                    name: 'pwd-protection',
                    value: showPassword,
                  }}
                  form={{
                    setFieldValue: (_, value) => onPasswordChange({ target: { checked: value } }),
                  }}
                />
                {showPassword && (
                  <Box>
                    <Flex sx={{ gap: '8px' }}>
                      <Box sx={{ position: 'relative', flexGrow: '1' }}>
                        <Input
                          type={pwdVisible ? 'text' : 'password'}
                          value={pwdValue}
                          placeholder="Enter password"
                          onChange={(event) => {
                            setPasswordChanged(false);
                            setPwdValue(event.target.value);
                          }}
                          data-testid="password"
                        />
                        <Box
                          onClick={() => setPwdVisible((current) => !current)}
                          sx={{
                            position: 'absolute',
                            top: '0',
                            bottom: '0',
                            right: '16px',
                            height: '16px',
                            margin: 'auto',
                            cursor: 'pointer',
                          }}
                        >
                          <Box as={pwdVisible ? Eye : EyeOff} width="16px" height="16px" aria-hidden="true" />
                        </Box>
                      </Box>
                      <Box maxWidth="87px">
                        <Button
                          variant="primary"
                          disabled={!pwdValue || passwordChanged}
                          data-testid="button-save"
                          loading={isLoading && !!pwdValue}
                          type="submit"
                          onClick={onPasswordEnabled}
                        >
                          Save
                        </Button>
                      </Box>
                    </Flex>
                  </Box>
                )}
              </Flex>
            </Flex>
            <Flex sx={{ gap: '8px', flexDirection: 'column' }}>
              <Text
                color="text.subtle"
                fontSize="12px"
                fontWeight="400"
                lineHeight="16px"
                letterSpacing="1px"
                sx={{ textTransform: 'uppercase' }}
              >
                Intros
              </Text>
              <IntroRequest
                value={list.introRequestManager?.id || user.id}
                onChange={onIntroRequestManagerChange}
                onDropDownStateChange={onDropDownStateChange}
                showIntroRequest={list.introRequestsEnabled}
                onShowIntroRequestChange={onShowIntroRequestChange}
              />
            </Flex>
            <Flex sx={{ gap: '8px', flexDirection: 'column' }}>
              <Text
                color="text.subtle"
                fontSize="12px"
                fontWeight="400"
                lineHeight="16px"
                letterSpacing="1px"
                sx={{ textTransform: 'uppercase' }}
              >
                Display settings
              </Text>
              <LayoutSelector
                value={list.shareableLayout}
                onChange={onLayoutChange}
                onDropDownStateChange={onDropDownStateChange}
                currentListView={currentView}
              />
            </Flex>
          </Flex>
          {list.passwordProtected && pwdValue && !passwordChanged && (
            <Box mt="8px">
              <Message content="The old password won’t work anymore after updating it." />
            </Box>
          )}
        </Box>
      )}

      {list.shareable && (
        <Box>
          <Box sx={{ height: '1px', backgroundColor: 'border.subtle', width: '100%', my: '24px' }} />
          <Button
            variant="secondary"
            size="small"
            loading={isLoading}
            onClick={() => {
              onEditSharedList({ shareable: false }, { closeModal: true });
            }}
          >
            Disable shared view
          </Button>
        </Box>
      )}
    </SideModal>
  );
};

ShareSharedListModal.propTypes = {
  list: ContactsSharedListSchema.isRequired,
  isLoading: PropTypes.bool,
  onEditSharedList: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ShareSharedListModal.defaultProps = {
  isLoading: false,
};
